// src/encounter/care-plan-tab/care-plan-view.tsx
// The encounter's "Care plan" tab (`?x=care-plan-tab`, it takes the header's Timeline slot): Samer's demo "protocol doc"
// (scrips-demo-samer.vercel.app ?view=protocol-doc) drawn from the record. Presentational: the plans come in as
// CarePlanView (contract C5 + C4 step, lib/care-plan-view.ts), the team as A2 rows; nothing is made up.
//  - Plan board (the demo's order): the care team strip, "Needs you now", then the 12-week panel (care-plan-weeks.tsx):
//    the six lanes x 12 weeks from the plan's ACTIVITIES (B1: lane, owner, weeks, status). Status labels: AI draft (a
//    label only, Andrew 4 Nov), Draft, Accepted · unsigned, Signed. A lane with none says "No plan items yet"; a server
//    that records no activities says so.
//  - Trajectory: the demo's goals table (measure, target and its source, latest result, status), as the C5 goal carries
//    them. The demo's per-measure charts need a result series the record does not hand this tab, so they are left out.
import { useRef, useState } from 'react';
import { Check, Circle, TriangleAlert } from 'lucide-react';
import { EmptyState, Segmented } from '@scripsteam/scrips-signal-ds';
import { cn } from '@/lib/utils';
import type { CarePlanActivityView, CarePlanView, GoalView } from '../lib/care-plan-view';
import type { ActivityAction } from './use-activity-action';
import { barId, currentPlanWeek, formatPlanDate, myLanesTitle, needsYouNow, type CarePlanViewer, type LaneFold } from './care-plan-board';
import { CP_BODY, CP_BODY_STRONG, CP_CARD_TITLE, CP_LABEL, CP_LINK_ACTION, CP_META, CP_WHITE_CARD, GOAL_TRACK_LABEL } from './care-plan-labels';
import { CarePlanTeamStrip, NeedsYouNowCard, type CarePlanTeamState } from './care-plan-team';
import { PlanWeeksPanel } from './care-plan-weeks';

function targetText(g: GoalView): string {
  if (!g.target) return g.targetSet ? 'Target not readable' : 'Target not set';
  return `${g.target.comparator} ${g.target.value} ${g.target.unit}`.trim();
}

/** The demo's title (XBe), from the goals that carry a target and the server's verdict on each. */
function trajectoryTitle(goals: readonly GoalView[]): string {
  const withTarget = goals.filter((g) => g.target);
  if (!withTarget.length) return 'No measure with a target on file';
  const outside = withTarget.filter((g) => g.status === 'not-met').length;
  return outside
    ? `${outside} of ${withTarget.length} measures with a target are outside it at the latest result`
    : `${withTarget.length} measures with a target: none outside it at the latest result`;
}

/** The demo's status word with its mark (Se): ✓ In target, ! Outside target, ○ No result / Not judged. */
function GoalStatusMark({ status }: { status: GoalView['status'] }) {
  if (status === 'met')
    return (
      <span className={cn(CP_LABEL, 'inline-flex items-center gap-1 text-status-good-text')}>
        <Check size={12} aria-hidden /> In target
      </span>
    );
  if (status === 'not-met')
    return (
      <span className={cn(CP_LABEL, 'inline-flex items-center gap-1 text-status-warning-text')}>
        <TriangleAlert size={12} aria-hidden /> Outside target
      </span>
    );
  return (
    <span className={cn(CP_LABEL, 'inline-flex items-center gap-1 text-ink-secondary')}>
      <Circle size={12} aria-hidden /> {status === 'no-data' ? 'No result' : 'Not judged'}
    </span>
  );
}

const FIRST_GOALS = 6;

export function TrajectoryView({ plan }: { plan: CarePlanView }) {
  const [all, setAll] = useState(false);
  const goals = all ? plan.goals : plan.goals.slice(0, FIRST_GOALS);
  return (
    <section aria-label="Trajectory" data-care-plan-trajectory className={cn(CP_WHITE_CARD, 'min-w-0')}>
      <header className="mb-2 min-w-0">
        <h2 data-traj-title className={CP_CARD_TITLE}>
          {trajectoryTitle(plan.goals)}
        </h2>
        <div data-traj-meta className={cn(CP_META, 'mt-1')}>
          {plan.period?.start
            ? `Plan start ${formatPlanDate(plan.period.start)}`
            : 'Plan start: not signed yet · the enrolment baseline is set when the plan is signed'}
          {plan.currentStep.state === 'known' ? ` · Step: ${plan.currentStep.name}` : ''}
        </div>
      </header>
      {plan.goals.length === 0 ? (
        <EmptyState icon="info" title="No goals on this plan" description="The care plan carries no goals yet." />
      ) : (
        <section aria-label="Goals" data-traj-goals className="mb-3">
          <h3 className={cn(CP_LABEL, 'm-0 mb-1 text-ink-secondary')}>Goals · {plan.goals.length}</h3>
          <table className="w-full border-collapse">
            <thead>
              <tr className={cn(CP_META, 'text-left')}>
                <th className="py-1 pr-3 font-medium">Measure</th>
                <th className="py-1 pr-3 font-medium">Target · set by · source</th>
                <th className="py-1 pr-3 font-medium">Latest value</th>
                <th className="py-1 font-medium">Status</th>
              </tr>
            </thead>
            <tbody>
              {goals.map((g) => (
                <tr key={g.id} data-goal-id={g.id} className="border-t border-surface-line align-top">
                  <td className={cn(CP_BODY_STRONG, 'py-2 pr-3')}>{g.title}</td>
                  <td className="py-2 pr-3">
                    <div className={cn(CP_BODY, 'tabular-nums')}>{targetText(g)}</div>
                    {g.target?.source ? <div className={CP_META}>{g.target.source}</div> : null}
                  </td>
                  <td className={cn(CP_BODY, 'py-2 pr-3 tabular-nums')}>
                    {g.latest ? (
                      <>
                        {`${g.latest.value ?? g.latest.valueText ?? ''} ${g.latest.unit}`.trim()}
                        <span className="text-ink-secondary"> · {formatPlanDate(g.latest.date)}</span>
                      </>
                    ) : (
                      'No result on file'
                    )}
                  </td>
                  <td className="py-2">
                    <GoalStatusMark status={g.status} />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {plan.goals.length > FIRST_GOALS ? (
            <button type="button" data-traj-goals-all onClick={() => setAll((v) => !v)} className={cn(CP_LINK_ACTION, 'mt-1')}>
              {all ? `Show the first ${FIRST_GOALS} goals` : `Show all ${plan.goals.length} goals`}
            </button>
          ) : null}
        </section>
      )}
    </section>
  );
}

/** The plan's goals by track, each with its sign-off ("Signed · who" / "Proposed · unsigned"): kept from the first board. */
function PlanGoalsCard({ plan }: { plan: CarePlanView }) {
  if (plan.goals.length === 0) return null;
  const byTrack = new Map<string, GoalView[]>();
  for (const g of plan.goals) {
    const t = g.track && GOAL_TRACK_LABEL[g.track] ? g.track : 'other';
    byTrack.set(t, [...(byTrack.get(t) ?? []), g]);
  }
  const signed = plan.goals.filter((g) => g.signoff).length;
  return (
    <section aria-label="Plan goals" data-plan-goals className={CP_WHITE_CARD}>
      <div className="flex items-baseline justify-between gap-4">
        <h2 className={CP_CARD_TITLE}>Goals · {plan.goals.length}</h2>
        <span className={CP_META}>
          {signed} of {plan.goals.length} goals signed
        </span>
      </div>
      {[...byTrack.entries()].map(([track, goals]) => (
        <div key={track} className="mt-3 flex flex-col gap-2" data-track={track}>
          <p className={cn(CP_LABEL, 'm-0 text-ink-secondary')}>{GOAL_TRACK_LABEL[track]}</p>
          <ul className="m-0 flex list-none flex-col gap-2 p-0">
            {goals.map((g) => (
              <li key={g.id} className="flex items-center justify-between gap-4">
                <span className={CP_BODY}>{g.title}</span>
                <span className={CP_META}>{g.signoff ? `Signed · ${g.signoff.signedBy}` : 'Proposed · unsigned'}</span>
              </li>
            ))}
          </ul>
        </div>
      ))}
    </section>
  );
}

export interface PlanBoardProps {
  plan: CarePlanView;
  /** Review (accept) or Sign one item; absent = the board is read-only. */
  onAction?: (carePlanId: string, activity: CarePlanActivityView, action: ActivityAction) => void;
  /** An Accept / Sign is in flight: every Review and Sign button is disabled. */
  busy?: boolean;
  /** The last refusal, with the plan it belongs to; shown only on that plan's board. */
  actionError?: { carePlanId: string; message: string } | null;
  /** The signed-in user ("Signed in as", Needs you now, the lanes that fold). Absent = nobody is known. */
  viewer?: CarePlanViewer | null;
  /** This plan's care team (A2). Absent = the strip is not drawn. */
  team?: CarePlanTeamState;
  /** Today, for the current plan week (tests pin it). */
  now?: Date;
}

export function PlanBoardView({ plan, onAction, busy = false, actionError = null, viewer = null, team, now }: PlanBoardProps) {
  const [fold, setFold] = useState<LaneFold>({});
  const board = useRef<HTMLDivElement>(null);
  const week = currentPlanWeek(plan.period?.start, now);
  const focus = needsYouNow(plan.activities ?? [], viewer, week);
  const act = onAction ? (a: CarePlanActivityView, kind: ActivityAction) => onAction(plan.carePlanId, a, kind) : undefined;
  const open = (a: CarePlanActivityView) => {
    setFold({ ...fold, [a.lane]: false });
    // After the lane opens, bring the bar into view and give it focus. A retest has no bar: its week's diamond (which carries
    // its id in data-retest-ids) opens instead, listing the item with its Review / Sign.
    window.setTimeout(() => {
      const bar = document.getElementById(barId(plan.carePlanId, a.id));
      const diamond = bar
        ? null
        : ([...(board.current?.querySelectorAll<HTMLButtonElement>('[data-retest-ids]') ?? [])].find((d) =>
            (d.dataset.retestIds ?? '').split(' ').includes(a.id),
          ) ?? null);
      const el = bar ?? diamond;
      el?.scrollIntoView?.({ block: 'center', behavior: 'smooth' });
      if (diamond && diamond.getAttribute('aria-expanded') !== 'true') diamond.click();
      el?.focus();
    }, 0);
  };
  return (
    <div ref={board} className="flex min-w-0 flex-col gap-2" data-plan-board={plan.carePlanId}>
      {team ? <CarePlanTeamStrip team={team} /> : null}
      <NeedsYouNowCard
        viewerName={viewer?.name ?? null}
        mineTitle={focus.myLanes.length ? myLanesTitle(focus.myLanes) : null}
        mine={focus.mine}
        week={week}
        thisWeek={focus.thisWeek}
        busy={busy}
        onAction={act}
        onOpen={open}
      />
      <PlanGoalsCard plan={plan} />
      <PlanWeeksPanel
        plan={plan}
        myLanes={focus.myLanes}
        week={week}
        viewerName={viewer?.name ?? null}
        viewerUserId={viewer?.userId ?? null}
        busy={busy}
        onAction={act}
        actionError={actionError && actionError.carePlanId === plan.carePlanId ? actionError.message : null}
        fold={fold}
        onFoldChange={setFold}
      />
    </div>
  );
}

export interface CarePlanTabViewProps extends Omit<PlanBoardProps, 'plan' | 'team'> {
  state: { kind: 'loading' } | { kind: 'error' } | { kind: 'empty' } | { kind: 'plans'; plans: CarePlanView[] };
  /** Each plan's care team (A2), by care plan id. */
  teams?: Record<string, CarePlanTeamState>;
}

export function CarePlanTabView({ state, teams, ...board }: CarePlanTabViewProps) {
  const [mode, setMode] = useState<'board' | 'trajectory'>('board');
  if (state.kind === 'loading') return <p className={cn(CP_META, 'm-0 p-6')}>Reading the care plan…</p>;
  if (state.kind === 'error')
    return <EmptyState icon="info" title="The care plan could not be read" description="Try again later. Nothing on the record has changed." />;
  if (state.kind === 'empty')
    return <EmptyState icon="info" title="No care plan" description="This patient is not on a care plan." />;
  return (
    <div className="flex min-w-0 flex-col gap-2 p-6" data-slot="care-plan-tab">
      <div data-care-plan-mode className="flex h-8 items-center">
        <Segmented
          aria-label="Care plan view"
          options={[
            { label: 'Plan board', value: 'board' },
            { label: 'Trajectory', value: 'trajectory' },
          ]}
          value={mode}
          onChange={(v: string) => setMode(v === 'trajectory' ? 'trajectory' : 'board')}
        />
      </div>
      {state.plans.map((p) =>
        mode === 'board' ? (
          <PlanBoardView key={p.carePlanId} plan={p} team={teams?.[p.carePlanId]} {...board} />
        ) : (
          <TrajectoryView key={p.carePlanId} plan={p} />
        ),
      )}
    </div>
  );
}
